/**
 * Fog of war: what one side may see. Pure and shared, so the server's
 * redaction and the app's rendering can never disagree.
 * Design and decisions: docs/fog-of-war/README.md.
 *
 * A **side** is the set of player slots whose vision is pooled: one player in
 * free-for-all games, a whole team once team play ships (`state.teams`, see
 * docs/team-games-plan.md). Everything here takes a side, never a single
 * player, so team play needs no change.
 *
 * **Vision** = the side's territories plus everything they can attack:
 * two-way borders (sea routes included) and outgoing one-way routes
 * (watchtowers), i.e. `borderReach`. Nobody sees back up a one-way route.
 */
import type { FogOfWar, GameConfig, GameEvent, PlayerId, TerritoryId, WireTerritory } from './index.js'
import { borderReach } from './gameLogic.js'

/** The adjacency slice of a map territory that vision needs. */
export interface FogMapTerritory {
  id: TerritoryId
  adjacencies: TerritoryId[]
  oneWayBorders?: TerritoryId[]
}

/** A game's fog mode. Absent (every pre-fog game) means `'none'`. */
export function fogOfWarOf(config: Partial<GameConfig> | null | undefined): FogOfWar {
  const fog = config?.fogOfWar
  return fog === 'light' || fog === 'heavy' ? fog : 'none'
}

export function isFogActive(config: Partial<GameConfig> | null | undefined): boolean {
  return fogOfWarOf(config) !== 'none'
}

/**
 * Heavy fog only supports random placement: a claim phase would have to show
 * every owner. Applied by the setup form and by the server on create, lobby
 * edit and start, so no path starts a heavy fog game with manual claim.
 */
export function withFogPlacement<C extends Partial<GameConfig>>(config: C): C {
  return fogOfWarOf(config) === 'heavy' && config.autoAssignTerritories !== true
    ? { ...config, autoAssignTerritories: true }
    : config
}

/**
 * The slots whose vision is pooled with `playerId`: the player plus allies.
 * Free-for-all games (no `teams`) return just the player.
 */
export function fogSide(state: { teams?: Record<PlayerId, string> } | null | undefined, playerId: PlayerId): PlayerId[] {
  const teams = state?.teams
  const team = teams?.[playerId]
  if (!teams || team === undefined) return [playerId]
  return Object.keys(teams).filter((slot) => teams[slot] === team)
}

/** Territory ids the side can see: its own and everything they can attack. */
export function visibleTerritoryIds(
  mapTerritories: readonly FogMapTerritory[],
  ownerOf: (territoryId: TerritoryId) => PlayerId | undefined,
  side: readonly PlayerId[],
): Set<TerritoryId> {
  const sideSet = new Set(side)
  const visible = new Set<TerritoryId>()
  for (const territory of mapTerritories) {
    const owner = ownerOf(territory.id)
    if (owner === undefined || !sideSet.has(owner)) continue
    visible.add(territory.id)
    for (const target of borderReach(territory)) visible.add(target)
  }
  return visible
}

/** The player whose action an event records (who a `hidden` placeholder names). */
function actorOf(event: GameEvent): PlayerId {
  const e = event as Record<string, unknown>
  const actor = e.player ?? e.attacker ?? e.eliminator
  return typeof actor === 'string' ? actor : ''
}

// `followUp` marks a conquest or post-conquest move: the log folds those into
// the attack's row, so every viewer's log has the same rows.
const hidden = (event: GameEvent): GameEvent => ({
  kind: 'hidden', player: actorOf(event), ts: event.ts,
  ...(event.kind === 'conquest' || event.kind === 'post-conquest-transfer' ? { followUp: true as const } : {}),
})

/** Remove the server-only `seenBy` from every event (finished fog games). */
export function stripFogAnnotations(events: readonly GameEvent[]): GameEvent[] {
  return events.map((event) => withoutSeen(event))
}

/** The territories an event touches on the board (what `seenBy` covers). */
function territoriesOf(event: GameEvent): TerritoryId[] {
  switch (event.kind) {
    case 'claim':
    case 'conquest':
      return [event.territoryId]
    case 'placement':
      return event.placements.map((p) => p.territoryId)
    case 'attack':
    case 'fortify':
    case 'post-conquest-transfer':
      return [event.fromTerritoryId, event.toTerritoryId]
    default:
      return []
  }
}

/** An event without its server-only `seenBy`. */
function withoutSeen<E extends GameEvent>(event: E): E {
  if (!('seenBy' in event)) return event
  const { seenBy: _seen, ...rest } = event as E & { seenBy?: unknown }
  return rest as E
}

/**
 * Server, when saving a fog game's events: record for each territory an event
 * touches which players could see it at that moment (`seenBy`), so each log
 * keeps what that player could see then, without replaying history and
 * without changing later as the fog moves. `ownerOf` is the board as saved.
 * Stripped from every copy a client receives.
 */
export function annotateFogVisibility(
  events: readonly GameEvent[],
  opts: { mapTerritories: readonly FogMapTerritory[]; ownerOf: (territoryId: TerritoryId) => PlayerId | undefined; players: readonly PlayerId[]; teams?: Record<PlayerId, string> },
): GameEvent[] {
  if (!events.some((e) => territoriesOf(e).length > 0)) return [...events]
  const vision = new Map(opts.players.map((p) => [p, visibleTerritoryIds(opts.mapTerritories, opts.ownerOf, fogSide({ teams: opts.teams }, p))]))
  return events.map((event) => {
    const ids = territoriesOf(event)
    if (ids.length === 0) return event
    const seenBy = Object.fromEntries(ids.map((id) => [id, opts.players.filter((p) => vision.get(p)?.has(id))]))
    return { ...event, seenBy } as GameEvent
  })
}

/**
 * The side's copy of the event log, 1:1 with the input so counts and absolute
 * indices stay aligned. Events the side couldn't see become `hidden`
 * placeholders; public events keep only their public fields. Unknown kinds
 * are hidden: a new event kind must opt in here to become visible.
 */
export function redactEventsForSide(events: readonly GameEvent[], side: readonly PlayerId[], fog: FogOfWar): GameEvent[] {
  if (fog === 'none') return [...events]
  const sideSet = new Set(side)
  const ours = (player: PlayerId | null | undefined) => player !== null && player !== undefined && sideSet.has(player)
  // Whether the side could see a territory when the event was saved. Events
  // saved before this annotation existed carry no `seenBy`: only the
  // involvement rules above apply to them.
  const saw = (event: GameEvent, territoryId: TerritoryId) =>
    ((event as { seenBy?: Record<TerritoryId, PlayerId[]> }).seenBy?.[territoryId] ?? []).some((p) => sideSet.has(p))
  return events.map((event): GameEvent => {
    switch (event.kind) {
      // Public: turn order, phases, resignations, takeovers, team
      // eliminations, the end.
      case 'phase-advance':
      case 'resign':
      case 'ai-takeover':
      case 'team-eliminated':
      case 'game-end':
        return event
      case 'turn-start': {
        // `armies` reveals territory count and region bonuses.
        if (ours(event.player)) return event
        const { armies: _armies, ...rest } = event
        return rest
      }
      case 'elimination': {
        // Public, but the inherited cards and bonus are counts fog hides.
        if (ours(event.eliminator)) return event
        const { cardsInherited: _cards, bonusGained: _bonus, ...rest } = event
        return rest
      }
      case 'attack': {
        if (ours(event.attacker) || ours(event.defender)) return withoutSeen(event)
        const seesFrom = saw(event, event.fromTerritoryId)
        const seesTo = saw(event, event.toTerritoryId)
        if (!seesFrom && !seesTo) return hidden(event)
        if (seesFrom && seesTo) return withoutSeen(event)
        // One end out of view (`unseen`): no dice, and only the losses on the
        // end that was seen (its troop count visibly dropped). Heavy fog
        // blanks the unseen end ('') and its player, unless the attack took
        // a seen territory (the conquest names them anyway). Light fog shows
        // owners, so both territories and players stay (the map can draw
        // the attack). Neutral values keep the event's shape.
        return {
          ...withoutSeen(event),
          unseen: seesFrom ? 'to' : 'from',
          fromTerritoryId: seesFrom || fog === 'light' ? event.fromTerritoryId : '',
          toTerritoryId: seesTo || fog === 'light' ? event.toTerritoryId : '',
          attacker: seesFrom || fog === 'light' || (seesTo && event.conquered) ? event.attacker : '',
          defender: seesTo || fog === 'light' ? event.defender : '',
          roll: { attacker: [], defender: [] },
          attackerLost: seesFrom ? event.attackerLost : 0,
          defenderLost: seesTo ? event.defenderLost : 0,
          conquered: seesTo && event.conquered,
        }
      }
      case 'conquest': {
        if (ours(event.attacker) || ours(event.defender) || saw(event, event.territoryId)) return withoutSeen(event)
        // Light fog: ownership is public, troops are not.
        if (fog === 'light') {
          const { armiesMoved: _moved, ...rest } = withoutSeen(event)
          return rest
        }
        return hidden(event)
      }
      case 'claim':
        // Claiming sets ownership, which light fog shows everywhere.
        return ours(event.player) || fog === 'light' || saw(event, event.territoryId) ? withoutSeen(event) : hidden(event)
      case 'placement': {
        if (ours(event.player)) return withoutSeen(event)
        const seen = event.placements.filter((p) => saw(event, p.territoryId))
        return seen.length > 0 ? { ...withoutSeen(event), placements: seen } : hidden(event)
      }
      case 'post-conquest-transfer':
      case 'fortify': {
        if (ours(event.player)) return withoutSeen(event)
        const seesFrom = saw(event, event.fromTerritoryId)
        const seesTo = saw(event, event.toTerritoryId)
        if (!seesFrom && !seesTo) return hidden(event)
        return { ...withoutSeen(event), fromTerritoryId: seesFrom ? event.fromTerritoryId : '', toTerritoryId: seesTo ? event.toTerritoryId : '' }
      }
      case 'card-draw':
      case 'card-turn-in':
        return ours(event.player) ? event : hidden(event)
      case 'hidden':
        return event
      default:
        return hidden(event)
    }
  })
}

type WireRecord = Record<string, unknown>
const pickKeys = (record: unknown, keep: ReadonlySet<string>) =>
  record && typeof record === 'object'
    ? Object.fromEntries(Object.entries(record as WireRecord).filter(([key]) => keep.has(key)))
    : record

/**
 * The side's share of the aggression ledger (`aggression[attacker][defender]`,
 * engine `GameState.aggression`). The side saw every attack it made or received
 * (both territories are in its vision), but attacks between two other sides may
 * have been out of view, so those entries are dropped: a fog client must not
 * learn who has been hitting whom behind the fog. Rows left empty are removed.
 */
export function redactAggressionForSide<L extends Record<string, Record<string, number>>>(
  aggression: L,
  side: readonly PlayerId[],
): L {
  const sideSet = new Set<string>(side)
  return Object.fromEntries(Object.entries(aggression)
    .map(([attacker, row]) => [attacker, Object.fromEntries(Object.entries(row ?? {})
      .filter(([defender]) => sideSet.has(attacker) || sideSet.has(defender)))] as const)
    .filter(([, row]) => Object.keys(row).length > 0)) as L
}

/**
 * The side's copy of a wire game state (the output of the server's
 * `normaliseStateForWire`). Removes everything the side may not see:
 * - territories outside vision → `{ id, fogged }` (heavy) or
 *   `{ id, ownerId, fogged }` (light), never armies;
 * - other players' card hands (counts included), banked bonus armies and
 *   claim armies;
 * - the starting board and the last conquest outside vision;
 * - the current player's per-turn card flags when they're not on the side;
 * - aggression-ledger entries between two other sides, via
 *   {@link redactAggressionForSide};
 * - events, via {@link redactEventsForSide}.
 * Every other field is public game progress and passes through.
 */
export function redactWireStateForSide(
  wire: WireRecord,
  opts: { side: readonly PlayerId[]; fog: FogOfWar; mapTerritories: readonly FogMapTerritory[] },
): WireRecord {
  const { side, mapTerritories } = opts
  if (opts.fog === 'none') return wire
  // Heavy fog games always deal territories at random (withFogPlacement), so
  // they have no claim phase. Kept for any heavy game that started claiming
  // before that rule: there it shows owners (like light fog), never troops.
  const fog: FogOfWar = opts.fog === 'heavy' && wire.phase === 'claim' ? 'light' : opts.fog
  const sideSet = new Set<string>(side)
  const territories = (wire.territories as WireTerritory[] | undefined) ?? []
  const ownerById = new Map(territories.map((t) => [t.id, t.ownerId]))
  const visible = visibleTerritoryIds(mapTerritories, (id) => ownerById.get(id), side)

  const redacted: WireRecord = { ...wire }
  redacted.territories = territories.map((t): WireTerritory =>
    visible.has(t.id)
      ? { id: t.id, ownerId: t.ownerId, armies: t.armies }
      : fog === 'light' ? { id: t.id, ownerId: t.ownerId, fogged: true } : { id: t.id, fogged: true })
  for (const key of ['cards', 'bonusArmies', 'armiesToPlace'] as const) {
    if (key in wire) redacted[key] = pickKeys(wire[key], sideSet)
  }
  if (Array.isArray(wire.initialTerritories)) {
    redacted.initialTerritories = (wire.initialTerritories as WireTerritory[]).filter((t) => visible.has(t.id))
  }
  const conquest = wire.lastConquest as { fromTerritoryId?: string; toTerritoryId?: string } | null | undefined
  if (conquest && !(visible.has(conquest.fromTerritoryId ?? '') && visible.has(conquest.toTerritoryId ?? ''))) {
    redacted.lastConquest = null
  }
  const players = wire.players as PlayerId[] | undefined
  const current = players?.[wire.turnIndex as number]
  if (current !== undefined && !sideSet.has(current)) {
    if ('conqueredThisTurn' in wire) redacted.conqueredThisTurn = false
    if ('mustTurnInCards' in wire) redacted.mustTurnInCards = false
  }
  if (wire.aggression && typeof wire.aggression === 'object') {
    redacted.aggression = redactAggressionForSide(wire.aggression as Record<string, Record<string, number>>, side)
  }
  if (Array.isArray(wire.events)) redacted.events = redactEventsForSide(wire.events as GameEvent[], side, fog)
  return redacted
}
